import { motion } from "framer-motion";
import { Globe, PhoneMissed, Bot, MessageCircle, CalendarCheck, Lock } from "lucide-react";

const cards = [
  {
    step: "01",
    title: "Intercept",
    desc: "The instant a web form is submitted or a call is missed, S1EP captures the signal — no lead ever slips through the cracks.",
    icons: [Globe, PhoneMissed],
    tags: ["Web Forms", "Missed Calls"],
  },
  {
    step: "02",
    title: "Engage",
    desc: "AI Voice fires alongside messages on every platform — WhatsApp, Instagram, Mail, Telegram and more — all at once, within seconds.",
    icons: [Bot, MessageCircle],
    tags: ["AI Voice", "WhatsApp", "Instagram", "Mail", "Telegram", "& more"],
  },
  {
    step: "03",
    title: "Secure",
    desc: "The qualified lead books a slot and the calendar locks in automatically — the deal is secured before a competitor even replies.",
    icons: [CalendarCheck, Lock],
    tags: ["Calendar", "Auto-Book"],
  },
];

export default function Architecture() {
  return (
    <section id="architecture" className="relative bg-pearl py-24 md:py-32">
      <div className="container-x">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          {/* sticky heading */}
          <div className="lg:sticky lg:top-32 lg:h-fit lg:self-start">
            <span className="eyebrow">The Architecture</span>
            <h2 className="mt-6 font-display text-5xl font-bold leading-[0.9] tracking-tight text-ink md:text-7xl">
              Zero-Latency
              <br />
              <span className="grad-text">Engagement.</span>
            </h2>
            <p className="mt-6 max-w-md text-lg text-ink-soft">
              Three stages. Milliseconds apart. A relentless pipeline engineered to close the gap between interest and action.
            </p>
            <div className="mt-8 hidden items-center gap-3 lg:flex">
              {cards.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-sm font-semibold text-ink-muted">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-xs text-accent-dark">{i + 1}</span>
                  {c.title}
                  {i < cards.length - 1 && <span className="mx-1 text-ink-faint">→</span>}
                </div>
              ))}
            </div>
          </div>

          {/* scrolling cards */}
          <div className="space-y-8">
            {cards.map((c) => (
              <motion.div
                key={c.step}
                initial={{ opacity: 0, y: 60, scale: 0.94 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-card md:p-10"
              >
                <div className="absolute right-6 top-4 font-display text-7xl font-bold text-slate-100 transition-colors group-hover:text-accent-soft md:text-8xl">
                  {c.step}
                </div>
                <div className="relative">
                  <div className="flex gap-3">
                    {c.icons.map((Icon, idx) => (
                      <span key={idx} className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent-dark transition-all duration-300 group-hover:bg-accent group-hover:text-white">
                        <Icon size={22} />
                      </span>
                    ))}
                  </div>
                  <h3 className="mt-6 font-display text-3xl font-bold text-ink">
                    {c.step.replace("0", "")}. {c.title}
                  </h3>
                  <p className="mt-3 max-w-md text-ink-soft">{c.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {c.tags.map((t) => (
                      <span key={t} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-ink-soft">{t}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
